import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';

import { residentialComplexesApi } from '../api/residentialComplexes';
import { bulkApi } from '../api/bulk';
import BulkImportPage from './BulkImportPage';
import AddressAutocompleteField from '../components/AddressAutocompleteField';
import { fetchResidentialComplexesFx } from '../store/complexes';
import { $objectKind } from '../store';
import { downloadBlobAsFile } from '../utils/download';
import { showSoftSuccess } from '../utils/softSuccess';
import { complexKindLabel, normalizeComplexKind } from '../utils/complexKind';

const createEmptyErrors = () => ({
  name: '',
  address: '',
});

const createEmptyForm = (kind = 'mdu') => ({
  name: '',
  address: '',
  complex_kind: normalizeComplexKind(kind),
});

const detailLabels = {
  city_with_type: 'Город',
  street_with_type: 'Улица',
  house: 'Дом',
};

const exportSelectSx = {
  '& .MuiInputLabel-root': {
    fontSize: 13,
    color: '#64748B',
  },
  '& .MuiOutlinedInput-root': {
    minHeight: 40,
    backgroundColor: '#F8FAFD',
    borderRadius: 2,
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.75)',
    '& fieldset': {
      borderColor: 'rgba(38, 65, 94, 0.18)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(46, 115, 184, 0.48)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2E73B8',
      borderWidth: 1,
    },
    '&.Mui-disabled': {
      backgroundColor: '#F3F5F8',
    },
  },
};

const exportSelectPaperSx = {
  mt: 0.6,
  border: '1px solid rgba(38, 65, 94, 0.18)',
  borderRadius: 2,
  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.14)',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox': {
    p: 0.5,
    maxHeight: 320,
  },
  '& .MuiAutocomplete-option': {
    minHeight: 36,
    mx: 0.25,
    my: 0.2,
    px: 1.25,
    borderRadius: 1.25,
    fontSize: 13,
    '&[aria-selected="true"]': {
      backgroundColor: 'rgba(46, 115, 184, 0.1)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(46, 115, 184, 0.08)',
    },
  },
};

const formatCoordinate = (value) => {
  if (!value) return '—';
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(6) : value;
};

const getMode = (value) => (value === 'import' ? 'import' : 'manual');

export default function AddResidentialComplex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const objectKind = useUnit($objectKind);
  const [form, setForm] = useState(() => createEmptyForm(objectKind));
  const [errors, setErrors] = useState(createEmptyErrors());
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [exportComplexes, setExportComplexes] = useState([]);
  const [exportObjectsLoading, setExportObjectsLoading] = useState(false);
  const [exportComplexId, setExportComplexId] = useState('');
  const [exportBuildingId, setExportBuildingId] = useState('');

  const mode = getMode(searchParams.get('mode'));
  const kind = normalizeComplexKind(objectKind);
  const kindLabel = complexKindLabel(kind);

  useEffect(() => {
    setForm((prev) => ({ ...prev, complex_kind: kind }));
  }, [kind]);

  useEffect(() => {
    if (mode !== 'import' || exportComplexes.length) return;
    let active = true;
    setExportObjectsLoading(true);
    residentialComplexesApi
      .getResidentialComplexes(0, 1000)
      .then((items) => {
        if (active) setExportComplexes(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        if (active) {
          setError(err?.response?.data?.detail || 'Не удалось загрузить список объектов');
        }
      })
      .finally(() => {
        if (active) setExportObjectsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mode, exportComplexes.length]);

  const selectedExportComplex = useMemo(
    () => exportComplexes.find((item) => String(item.id) === exportComplexId) || null,
    [exportComplexId, exportComplexes]
  );
  const exportBuildings = selectedExportComplex?.buildings || [];
  const selectedExportBuilding = useMemo(
    () => exportBuildings.find((item) => String(item.id) === exportBuildingId) || null,
    [exportBuildingId, exportBuildings]
  );

  const validate = (data) => {
    const next = createEmptyErrors();
    if (!data.name?.trim()) next.name = 'Название обязательно к заполнению';
    if (!data.address?.trim()) next.address = 'Адрес обязателен к заполнению';
    return next;
  };

  const handleModeChange = (_, nextMode) => {
    if (!nextMode || nextMode === mode) return;
    setSearchParams(nextMode === 'import' ? { mode: 'import' } : {});
  };

  const handleFormValue = (field, value) => {
    const nextForm = { ...form, [field]: value };
    setForm(nextForm);
    setErrors(validate(nextForm));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationResult = validate(form);
    setErrors(validationResult);
    if (Object.values(validationResult).some(Boolean)) return;

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const lat = Number(selectedSuggestion?.data?.geo_lat);
      const lon = Number(selectedSuggestion?.data?.geo_lon);
      await residentialComplexesApi.createResidentialComplex({
        name: form.name.trim(),
        address: form.address.trim(),
        latitude: Number.isFinite(lat) ? lat : null,
        longitude: Number.isFinite(lon) ? lon : null,
        complex_kind: kind,
      });
      setSuccess(`${kindLabel} добавлен`);
      showSoftSuccess(`${kindLabel} добавлен`);
      fetchResidentialComplexesFx();
      setForm(createEmptyForm(kind));
      setErrors(createEmptyErrors());
      setSelectedSuggestion(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось создать объект');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportObjects = async () => {
    setError('');
    setSuccess('');
    setExporting(true);
    try {
      const { blob, filename } = await bulkApi.exportObjects({
        ...(exportComplexId ? { residential_complex_id: exportComplexId } : {}),
        ...(exportBuildingId ? { building_id: exportBuildingId } : {}),
      });
      downloadBlobAsFile(blob, filename);
      setSuccess('Выгрузка готова');
      showSoftSuccess('Выгрузка готова');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось сформировать выгрузку');
    } finally {
      setExporting(false);
    }
  };

  const addressDetails = useMemo(() => {
    const data = selectedSuggestion?.data;
    if (!data) return [];
    return Object.entries(detailLabels)
      .map(([key, label]) => ({ label, value: data[key] }))
      .filter((item) => item.value);
  }, [selectedSuggestion]);

  const hasAddressMeta = Boolean(
    selectedSuggestion?.value ||
      addressDetails.length ||
      selectedSuggestion?.data?.geo_lat ||
      selectedSuggestion?.data?.geo_lon
  );

  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 2.5 },
        py: { xs: 1.5, md: 2 },
        width: '100%',
      }}
    >
      <Stack spacing={2}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            px: { xs: 1, md: 1.25 },
            py: 0.9,
            backgroundColor: '#fff',
          }}
        >
          <Tabs
            value={mode}
            onChange={handleModeChange}
            sx={{
              minHeight: 0,
              p: 0.4,
              borderRadius: 2.5,
              backgroundColor: 'rgba(15, 23, 42, 0.035)',
              display: 'inline-flex',
              border: '1px solid rgba(148, 163, 184, 0.18)',
              '& .MuiTabs-flexContainer': {
                gap: 4,
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                minHeight: 0,
                minWidth: { xs: 118, md: 142 },
                px: 1.75,
                py: 0.95,
                borderRadius: 1.8,
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: 'text.secondary',
                border: '1px solid transparent',
                transition: 'background-color 180ms ease, color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
              },
              '& .MuiTab-root.Mui-selected': {
                color: 'text.primary',
                backgroundColor: '#fff',
                borderColor: 'rgba(59, 130, 246, 0.34)',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
              },
              '& .MuiTab-root:hover': {
                color: 'text.primary',
                backgroundColor: 'rgba(255, 255, 255, 0.76)',
              },
            }}
          >
            <Tab value="manual" label="Вручную" />
            <Tab value="import" label="Из файла" />
          </Tabs>
        </Paper>

        {mode === 'manual' ? (
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                px: { xs: 2, md: 3 },
                py: { xs: 2, md: 2.5 },
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                  Добавить объект
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
                  Укажите название и адрес жилого комплекса.
                </Typography>
              </Stack>
            </Paper>

            <Paper
              component="form"
              onSubmit={handleSubmit}
              noValidate
              autoComplete="off"
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Stack spacing={0}>
                <Box sx={{ px: { xs: 2, md: 3 }, py: 2.25 }}>
                  <Stack spacing={0.75}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Основные данные
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Заполните название и адрес.
                    </Typography>
                  </Stack>
                </Box>

                <Divider />

                <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}>
                  <Stack spacing={2}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Тип клиента
                      </Typography>
                      <Chip size="small" label={kindLabel} color={kind === 'settlement' ? 'success' : 'default'} variant="outlined" />
                    </Stack>

                    <TextField
                      label="Название"
                      value={form.name}
                      onChange={(event) => handleFormValue('name', event.target.value)}
                      required
                      fullWidth
                      autoComplete="off"
                      name="complex_name"
                      error={Boolean(errors.name)}
                      helperText={
                        errors.name
                          || (kind === 'settlement'
                            ? 'Например: ДНП Солнечное'
                            : 'Например: ЖК Новоантропшино')
                      }
                    />

                    <AddressAutocompleteField
                      label="Адрес"
                      value={form.address}
                      onValueChange={(value) => {
                        handleFormValue('address', value);
                        if (!value.trim()) {
                          setSelectedSuggestion(null);
                        }
                      }}
                      onSuggestionSelect={(suggestion) => {
                        setSelectedSuggestion(suggestion);
                        if (suggestion?.value) {
                          handleFormValue('address', suggestion.value);
                        }
                      }}
                      required
                      name="complex_address"
                      error={Boolean(errors.address)}
                      helperText={errors.address || 'Начните вводить адрес.'}
                    />

                    {hasAddressMeta && (
                      <Paper
                        variant="outlined"
                        sx={{
                          borderRadius: 2.5,
                          px: 2,
                          py: 1.5,
                          backgroundColor: 'rgba(15, 23, 42, 0.02)',
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Выбранный адрес
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedSuggestion?.value || form.address || '—'}
                          </Typography>

                          {addressDetails.length > 0 && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {addressDetails.map((item) => (
                                <Chip
                                  key={item.label}
                                  size="small"
                                  variant="outlined"
                                  label={`${item.label}: ${item.value}`}
                                />
                              ))}
                            </Stack>
                          )}

                          {(selectedSuggestion?.data?.geo_lat || selectedSuggestion?.data?.geo_lon) && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <Typography variant="caption" color="text.secondary">
                                Широта:{' '}
                                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                  {formatCoordinate(selectedSuggestion?.data?.geo_lat)}
                                </Box>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Долгота:{' '}
                                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                  {formatCoordinate(selectedSuggestion?.data?.geo_lon)}
                                </Box>
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                </Box>

                <Divider />

                <Box
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: 1.75,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    backgroundColor: 'rgba(15, 23, 42, 0.015)',
                  }}
                >
                  <Button type="submit" variant="contained" disabled={submitting} sx={{ minWidth: 180 }}>
                    {submitting ? 'Сохранение...' : 'Создать объект'}
                  </Button>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                px: { xs: 2, md: 3 },
                py: { xs: 1.75, md: 2 },
                backgroundColor: '#fff',
              }}
            >
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Выгрузка данных
                  </Typography>
                </Box>
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  spacing={1.25}
                  alignItems={{ xs: 'stretch', lg: 'center' }}
                >
                  <Autocomplete
                    size="small"
                    options={exportComplexes}
                    value={selectedExportComplex}
                    loading={exportObjectsLoading}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="Нет объектов"
                    loadingText="Загрузка..."
                    slotProps={{ paper: { sx: exportSelectPaperSx } }}
                    onChange={(_, value) => {
                      setExportComplexId(value?.id ? String(value.id) : '');
                      setExportBuildingId('');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={kind === 'settlement' ? 'Посёлок' : 'ЖК'}
                        placeholder="Все объекты"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {exportObjectsLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ minWidth: { lg: 280 }, flex: 1, ...exportSelectSx }}
                  />
                  <Autocomplete
                    size="small"
                    options={exportBuildings}
                    value={selectedExportBuilding}
                    disabled={!selectedExportComplex}
                    getOptionLabel={(option) => option?.address || option?.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="Нет домов"
                    slotProps={{ paper: { sx: exportSelectPaperSx } }}
                    onChange={(_, value) =>
                      setExportBuildingId(value?.id ? String(value.id) : '')
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Дом"
                        placeholder={selectedExportComplex ? 'Все дома' : 'Сначала выберите объект'}
                      />
                    )}
                    sx={{ minWidth: { lg: 320 }, flex: 1.2, ...exportSelectSx }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleExportObjects}
                    disabled={exporting || exportObjectsLoading}
                    sx={{ minWidth: 210, minHeight: 40 }}
                  >
                    {exporting ? 'Формируем...' : 'Выгрузить'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
            <BulkImportPage showExportAction={false} />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
